import type { UserProfile } from '@/lib/auth/client';
import { useAuth, useUser } from '@/lib/auth/react';
import { useAuthenticationMe } from '@/lib/endpoints';

const BYPASS_AUTH = import.meta.env.VITE_BYPASS_AUTH === 'true';

/**
 * Mock user surfaced when `VITE_BYPASS_AUTH=true`. Mapped from the Firebase
 * Auth REST shape for `daniel@sunfltd.com` to the `UserProfile` interface the
 * UI consumes — Firebase's `localId` becomes `firebaseUid`, `photoUrl` becomes
 * `profileImage`, the verified `phone` provider sets `phoneVerified: true`,
 * and the millisecond timestamps are converted to ISO strings.
 */
export const BYPASS_AUTH_MOCK_USER: UserProfile = {
  userId: 1,
  firebaseUid: 'GcdOpC0Ka4bP2Pf3hReSVX8RpfD3',
  uniqueId: 'GcdOpC0Ka4bP2Pf3hReSVX8RpfD3',
  email: 'daniel@sunfltd.com',
  isEmailVerified: true,
  firstName: 'Daniel',
  lastName: 'Betesh',
  displayName: 'Daniel Betesh',
  nickname: 'Daniel',
  phone: '+972523146902',
  isPhoneVerified: true,
  profileImage: 'https://lh3.googleusercontent.com/a/ACg8ocIAzijAOHo7lY19TQ13MQHL8WFly0KLn3munCayJoRn=s96-c',
  isActive: true,
  createdAt: new Date(1695036768853).toISOString(),
  lastLoginDate: new Date(1778416259907).toISOString(),
  isAddressVerified: true,
};

/**
 * Drop-in replacement for `useUser()` from `@/lib/auth/react`
 * that returns a mock user when `VITE_BYPASS_AUTH=true`, and otherwise overlays
 * the **live** server verification state from `GET /auth/me` onto the auth-client
 * profile.
 *
 * Why the overlay: on a page refresh the auth client restores the *stored*
 * token-claim profile and never re-fetches, so the signup/verification flags can
 * be stale. `/auth/me` is the server's source of truth, so the signup funnel
 * resumes at the right step and the app-wide "logged in = onboarded" gates stay
 * accurate. React Query's in-memory cache is empty after a refresh, so `/me` is
 * fetched fresh on load; within a session it is invalidated after onboarding.
 *
 * Verification flags are monotonic (false → true, never back), so we OR each one
 * across the two sources: whichever has observed a step as done wins. That way a
 * cached `/me` can't mask an in-session verify, nor a stale stored profile mask a
 * step the server already recorded.
 */
export function useAppUser(): UserProfile | null {
  const user = useUser();
  const { isAuthenticated } = useAuth();
  const me = useAuthenticationMe({ query: { enabled: !BYPASS_AUTH && isAuthenticated } });

  if (BYPASS_AUTH) return BYPASS_AUTH_MOCK_USER;
  if (!user) return null;

  const live = me.data?.profile;
  if (!live) return user;
  return {
    ...user,
    isEmailVerified: (user.isEmailVerified ?? false) || live.isEmailVerified,
    isPhoneVerified: user.isPhoneVerified || live.isPhoneVerified,
    isAddressVerified: user.isAddressVerified || live.isAddressVerified,
  };
}
